import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useLanguage, useTranslation } from "../contexts/LanguageContext";
import BottomNav from "../components/BottomNav";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { 
  Settings, Edit, Star, Zap, Eye, 
  MapPin, LogOut, ChevronRight, BadgeCheck, MessageCircle, Bell, Globe,
  Heart, ShieldOff
} from "lucide-react";

import { API } from "../config";

// Drapeaux SVG
const FlagFR = () => (
  <svg viewBox="0 0 36 36" className="w-5 h-5">
    <rect fill="#002395" width="12" height="36"/>
    <rect fill="#FFFFFF" x="12" width="12" height="36"/>
    <rect fill="#ED2939" x="24" width="12" height="36"/>
  </svg>
);

const FlagGB = () => (
  <svg viewBox="0 0 36 36" className="w-5 h-5">
    <rect fill="#00247D" width="36" height="36"/>
    <path fill="#FFFFFF" d="M0,0 L36,36 M36,0 L0,36" stroke="#FFFFFF" strokeWidth="6"/>
    <path fill="#CF142B" d="M0,0 L36,36 M36,0 L0,36" stroke="#CF142B" strokeWidth="2"/>
    <path fill="#FFFFFF" d="M18,0 V36 M0,18 H36" stroke="#FFFFFF" strokeWidth="10"/>
    <path fill="#CF142B" d="M18,0 V36 M0,18 H36" stroke="#CF142B" strokeWidth="6"/>
  </svg>
);

export default function Profile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { language, toggleLanguage } = useLanguage();
  const [profilePhoto, setProfilePhoto] = useState(null);

  // Charger la photo de profil principale
  useEffect(() => {
    const loadProfilePhoto = async () => {
      // Priorité: main_photo > première photo > picture Google
      const photoUrl = user?.main_photo || user?.photos?.[0];
      
      if (photoUrl && photoUrl.startsWith("/api/photos/")) {
        try {
          const token = localStorage.getItem("session_token");
          const response = await fetch(`${API}/photos/${photoUrl.replace("/api/photos/", "")}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setProfilePhoto(data.data);
          }
        } catch (err) {
          console.error("Error loading profile photo:", err);
        }
      } else if (photoUrl) {
        setProfilePhoto(photoUrl);
      } else {
        setProfilePhoto(user?.picture);
      }
    };
    
    if (user) {
      loadProfilePhoto();
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    
    localStorage.removeItem("user");
    localStorage.removeItem("session_token");
    alert(language === 'fr' ? "Déconnexion réussie" : "Logged out successfully");
    navigate("/");
  };

  const isProfileComplete = user?.name && user?.age && user?.bio && user?.location;

  return (
    <div className="min-h-screen bg-background pb-safe" data-testid="profile-page">
      
      <div className="pt-4 sm:pt-6 pb-20 sm:pb-24 px-3 sm:px-4">
        <div className="max-w-lg mx-auto">
          {/* Language Selector - Top Right */}
          <div className="flex justify-end mb-3 sm:mb-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-card/50 backdrop-blur-sm border border-white/10 rounded-full hover:border-primary/50 transition-all touch-target"
              title={t('profile.changeLanguage')}
              data-testid="language-toggle"
            >
              {language === 'fr' ? <FlagGB /> : <FlagFR />}
              <span className="text-xs sm:text-sm text-muted-foreground">
                {language === 'fr' ? 'EN' : 'FR'}
              </span>
              <Globe className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground" />
            </button>
          </div>

          {/* Profile Incomplete Warning */}
          {!isProfileComplete && (
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <Edit className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-yellow-500 font-medium text-xs sm:text-sm">
                  {language === 'fr' ? 'Profil incomplet' : 'Incomplete profile'}
                </p>
                <p className="text-yellow-500/70 text-[10px] sm:text-xs truncate">
                  {language === 'fr' ? 'Complétez votre profil pour être visible' : 'Complete your profile to be visible'}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => navigate("/profile/edit")}
                className="bg-yellow-500 text-black rounded-full font-bold text-xs sm:text-sm px-3 sm:px-4 flex-shrink-0"
              >
                {language === 'fr' ? 'Compléter' : 'Complete'}
              </Button>
            </div>
          )}

          {/* Profile Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="relative inline-block mb-3 sm:mb-4">
              <Avatar className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-primary">
                <AvatarImage src={profilePhoto} />
                <AvatarFallback className="bg-primary text-white text-3xl sm:text-4xl">
                  {user?.name?.[0]}
                </AvatarFallback>
              </Avatar>
              {user?.is_verified && (
                <div className="absolute -bottom-1 -right-1 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-500 flex items-center justify-center border-4 border-background">
                  <BadgeCheck className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              )}
              <button
                onClick={() => navigate("/profile/edit")}
                className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white shadow-lg"
                data-testid="edit-profile-btn"
                style={{ display: user?.is_verified ? 'none' : 'flex' }}
              >
                <Edit className="w-5 h-5" />
              </button>
              {/* Bouton aperçu du profil */}
              <button
                onClick={() => navigate("/profile/preview")}
                className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-lg hover:bg-white/30 transition-colors"
                data-testid="preview-profile-btn"
                title={t('profile.previewProfile')}
              >
                <Eye className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-1">
              <h1 className="font-heading text-3xl font-bold text-white">
                {user?.name}{user?.age ? `, ${user.age}` : ""}
              </h1>
              {user?.is_verified && (
                <BadgeCheck className="w-6 h-6 text-green-500" />
              )}
            </div>
            
            {user?.location && (
              <div className="flex items-center justify-center gap-1 text-muted-foreground">
                <MapPin className="w-4 h-4" />
                <span>{user.location}</span>
              </div>
            )}
          </div>

          {/* Verification Banner */}
          {!user?.is_verified && (
            <div 
              onClick={() => navigate("/verification")}
              className="bg-green-500/20 border border-green-500/50 rounded-2xl p-4 mb-6 flex items-center gap-4 cursor-pointer hover:bg-green-500/30 transition-all"
              data-testid="verification-banner"
            >
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <BadgeCheck className="w-6 h-6 text-green-500" />
              </div>
              <div className="flex-1">
                <p className="text-green-500 font-semibold">
                  {language === 'fr' ? 'Vérifiez votre profil' : 'Verify your profile'}
                </p>
                <p className="text-green-500/70 text-sm">
                  {language === 'fr' ? 'Obtenez le badge vérifié' : 'Get the verified badge'}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-green-500" />
            </div>
          )}

          {/* Credits & Messages Card */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div 
              onClick={() => navigate("/credits")}
              className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-4 cursor-pointer hover:border-primary/50 transition-all"
              data-testid="credits-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl gradient-gold flex items-center justify-center">
                  <Star className="w-5 h-5 text-black fill-current" />
                </div>
                <span className="text-muted-foreground text-sm">{t('credits.title')}</span>
              </div>
              <p className="text-2xl font-bold text-gradient-gold">{user?.credits || 0}</p>
            </div>

            <div className="bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-muted-foreground text-sm">
                  {language === 'fr' ? 'Messages gratuits' : 'Free messages'}
                </span>
              </div>
              <p className="text-2xl font-bold text-blue-500">{user?.free_messages_today ?? 5}/5</p>
            </div>
          </div>

          {/* Menu Items */}
          <div className="space-y-2">
            <MenuItem 
              icon={<Edit className="w-5 h-5" />}
              label={t('profile.editProfile')}
              onClick={() => navigate("/profile/edit")}
              testId="edit-profile-menu"
            />
            <MenuItem 
              icon={<Bell className="w-5 h-5" />}
              label={t('notifications.title')}
              onClick={() => navigate("/notifications")}
              testId="notifications-menu"
            />
            <MenuItem 
              icon={<Eye className="w-5 h-5" />}
              label={t('likes.title')}
              onClick={() => navigate("/who-liked-me")}
              badge="Premium"
              testId="who-liked-menu"
            />
            <MenuItem 
              icon={<Heart className="w-5 h-5 text-yellow-500" />}
              label={language === 'fr' ? 'Mes favoris' : 'My favorites'}
              onClick={() => navigate("/favorites")}
              testId="favorites-menu"
            />
            <MenuItem 
              icon={<Zap className="w-5 h-5" />}
              label={language === 'fr' ? 'Booster mon profil' : 'Boost my profile'}
              onClick={() => navigate("/credits")}
              badge={language === 'fr' ? '20 crédits' : '20 credits'}
              testId="boost-menu"
            />
            <MenuItem 
              icon={<Star className="w-5 h-5" />}
              label={t('credits.buyCredits')}
              onClick={() => navigate("/credits")}
              testId="buy-credits-menu"
            />
            <MenuItem 
              icon={<Settings className="w-5 h-5" />}
              label={t('settings.title')}
              onClick={() => navigate("/settings")}
              testId="settings-menu"
            />
            <MenuItem 
              icon={<ShieldOff className="w-5 h-5 text-red-400" />}
              label={language === 'fr' ? 'Membres bloqués' : 'Blocked users'}
              onClick={() => navigate("/blocked-users")}
              testId="blocked-users-menu"
            />
            <MenuItem 
              icon={<LogOut className="w-5 h-5 text-red-500" />}
              label={t('auth.logout')}
              onClick={handleLogout}
              danger
              testId="logout-menu"
            />
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}

function MenuItem({ icon, label, onClick, badge, danger, testId }) {
  return (
    <button
      onClick={onClick}
      className={`w-full bg-card/50 backdrop-blur-sm border border-white/5 rounded-2xl p-4 flex items-center gap-4 hover:border-primary/50 transition-all ${
        danger ? "hover:border-red-500/50" : ""
      }`}
      data-testid={testId}
    >
      <div className={`${danger ? "text-red-500" : "text-muted-foreground"}`}>
        {icon}
      </div>
      <span className={`flex-1 text-left ${danger ? "text-red-500" : "text-white"}`}>
        {label}
      </span>
      {badge && (
        <span className="px-2 py-1 bg-secondary/20 text-secondary rounded-full text-xs font-bold">
          {badge}
        </span>
      )}
      <ChevronRight className={`w-5 h-5 ${danger ? "text-red-500" : "text-muted-foreground"}`} />
    </button>
  );
}
